import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

const APP_DISPLAY_NAMES: Record<string, string> = {
  mmdz: "My Music Distribution Zone",
  mmpz: "My Music Publishing Zone",
};

const APP_LOGOS: Record<string, string> = {
  mmdz: "/MMDZ-logo-round.png",
  mmpz: "/MMPZ-logo-round.png",
};

interface RedirectingScreenProps {
  url: string;
  appKey: string;
  profileName?: string;
  logoUrl?: string;
  cancelLabel?: string;
  onCancel: () => void;
}

const RedirectingScreen = ({ url, appKey, profileName, logoUrl, cancelLabel = "Back to login", onCancel }: RedirectingScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [redirectFailed, setRedirectFailed] = useState(false);
  const rafRef = useRef<number>();

  useEffect(() => {
    const duration = 3500;
    const start = Date.now();

    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / duration) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        try {
          window.location.replace(url);
        } catch {
          setRedirectFailed(true);
        }
        setTimeout(() => setRedirectFailed(true), 60000);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [url]);

  const appName = profileName || (APP_DISPLAY_NAMES[appKey] ?? appKey);
  const logo = logoUrl || APP_LOGOS[appKey];

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        background: "linear-gradient(145deg, #0d1a26 0%, #152338 50%, #0b1821 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        overflow: "hidden",
      }}
    >
      {[55, 72, 90].map((size, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${-12 - i * 6}%`,
            top: "50%",
            transform: "translateY(-50%)",
            width: `${size}vh`,
            height: `${size}vh`,
            borderRadius: "50%",
            border: `1px solid rgba(51,153,254,${0.05 - i * 0.015})`,
            pointerEvents: "none",
          }}
        />
      ))}

      <Stack alignItems="center" spacing={3} sx={{ position: "relative", zIndex: 1 }}>
        {logo && (
          <Box
            sx={{
              "@keyframes pulse-glow": {
                "0%": { boxShadow: "0 0 0 0 rgba(51,153,254,0.35)", borderRadius: "50%" },
                "70%": { boxShadow: "0 0 0 22px rgba(51,153,254,0)", borderRadius: "50%" },
                "100%": { boxShadow: "0 0 0 0 rgba(51,153,254,0)", borderRadius: "50%" },
              },
              animation: "pulse-glow 2s ease-in-out infinite",
              borderRadius: "50%",
            }}
          >
            <img
              src={logo}
              alt={appName}
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                border: "2px solid rgba(51,153,254,0.55)",
                boxShadow: "0 0 0 8px rgba(51,153,254,0.1), 0 20px 56px rgba(0,0,0,0.55)",
                display: "block",
              }}
            />
          </Box>
        )}

        <Stack alignItems="center" spacing={0.75}>
          <Typography
            variant="h6"
            sx={{ color: "rgba(255,255,255,0.92)", fontWeight: 700, letterSpacing: "-0.3px" }}
          >
            {appName}
          </Typography>
          <Typography sx={{ color: redirectFailed ? "rgba(251,140,0,0.8)" : "rgba(255,255,255,0.4)", fontSize: 13 }}>
            {redirectFailed ? "Redirect didn't open automatically." : "You're all set, please wait…"}
          </Typography>
        </Stack>

        <Box sx={{ width: 240, mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 3,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.08)",
              "& .MuiLinearProgress-bar": {
                background: redirectFailed
                  ? "linear-gradient(90deg, #fb8c00, #ffa726)"
                  : "linear-gradient(90deg, #3399FE, #66b2ff)",
                borderRadius: 2,
                boxShadow: redirectFailed
                  ? "0 0 10px rgba(251,140,0,0.7)"
                  : "0 0 10px rgba(51,153,254,0.7)",
              },
            }}
          />
        </Box>

        <Stack spacing={1.5} alignItems="center" sx={{ width: 240, mt: 1 }}>
          {redirectFailed && (
            <Button
              component="a"
              href={url}
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: "#3399FE",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: 14,
                textTransform: "none",
                py: 1.25,
                "&:hover": { backgroundColor: "#1a77d4" },
              }}
            >
              Open {appName}
            </Button>
          )}
          <Button
            fullWidth
            variant="text"
            onClick={onCancel}
            sx={{
              color: "rgba(255,255,255,0.35)",
              fontSize: 13,
              textTransform: "none",
              "&:hover": { color: "rgba(255,255,255,0.6)", backgroundColor: "transparent" },
            }}
          >
            {cancelLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default RedirectingScreen;
